import React, { useContext } from 'react';
import { Button, Col, ConfigProvider, Form, Input, Modal, Row, Select, DatePicker, Typography, Space, Upload, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps, RcFile } from 'antd/es/upload/interface';
import dayjs from 'dayjs';
import locale from "antd/es/locale/es_ES";
import { PostProjecte } from "../../../dao/abp/PostProjecte";
import { AuthContext } from "../../../config/AuthContext";
import AvaluacioColors from "../../../styles/AvaluacioColors";
import { GetUserAules } from "../../../dao/abp/GetUserAules";
import { GetProjecteImagen } from "../../../dao/GetProjecteImagen";

interface Props {
    element: any;
    visible: boolean;
    onClose: (value: boolean) => void;
    editar?: boolean;
}

const ProjecteModal: React.FC<Props> = (props: Props) => {
    const [form] = Form.useForm();
    const { isLoggedIn, userId, user, setBreadCrumb } = useContext(AuthContext);
    const [aules, setAules] = React.useState<any[]>([]);
    const [imageUrl, setImageUrl] = React.useState<string>();
    const [fileList, setFileList] = React.useState<UploadFile[]>([]);

    React.useEffect(() => {
        if (isLoggedIn && userId) {
            GetUserAules(userId).then(setAules);
        }
    }, [isLoggedIn, userId]);

    React.useEffect(() => {
        if (props.visible && props.element) {
            console.log("ProjecteModal - props.element:", props.element);

            // Parse id_aules: can come as Array, comma-separated string, or fall back to id_aula
            let parsedAules: number[] = [];
            if (Array.isArray(props.element.id_aules)) {
                parsedAules = props.element.id_aules.map((id: any) => parseInt(id)).filter((id: number) => !isNaN(id));
            } else if (typeof props.element.id_aules === 'string' && props.element.id_aules) {
                parsedAules = props.element.id_aules.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
            } else if (props.element.id_aula) {
                const raw = props.element.id_aula;
                if (Array.isArray(raw)) {
                    parsedAules = raw.map((id: any) => parseInt(id)).filter((id: number) => !isNaN(id));
                } else if (typeof raw === 'string') {
                    parsedAules = raw.split(',').map((id: string) => parseInt(id.trim())).filter((id: number) => !isNaN(id));
                } else {
                    parsedAules = [raw];
                }
            }

            const initVal = {
                ...props.element,
                estat: props.element.percentatge?.estat ?? props.element.estat,
                data_ini: props.element.data_ini ? dayjs(props.element.data_ini) : null,
                data_fi: props.element.data_fi ? dayjs(props.element.data_fi) : null,
                id_aules: parsedAules,
                escala_validacio: props.element.escala_validacio ?? 5,
            };
            console.log("ProjecteModal - initVal:", initVal);
            form.setFieldsValue(initVal);

            if (props.element.id) {
                GetProjecteImagen(props.element.id + "").then((res: any) => {
                    if (res?.json) {
                        const cleaned = res.json.split(':').pop();
                        setImageUrl(`data:image/png;base64,${cleaned}`);
                    } else {
                        setImageUrl(undefined);
                    }
                });
            } else {
                setImageUrl(undefined);
            }
            setFileList([]);
        } else {
            form.resetFields();
            setImageUrl(undefined);
            setFileList([]);
        }
    }, [props.element, props.visible, form]);

    const getBase64 = (img: RcFile, callback: (url: string) => void) => {
        const reader = new FileReader();
        reader.addEventListener('load', () => callback(reader.result as string));
        reader.readAsDataURL(img);
    };

    const handleUploadChange: UploadProps['onChange'] = (info) => {
        let newFileList = [...info.fileList];
        newFileList = newFileList.slice(-1);
        setFileList(newFileList);
        if (newFileList.length > 0 && newFileList[0].originFileObj) {
            getBase64(newFileList[0].originFileObj as RcFile, (url) => {
                setImageUrl(url);
            });
        }
    };

    async function handleSubmit() {
        try {
            const values = await form.validateFields();
            const formData = { ...values };

            // Send dates as ISO strings with time
            if (formData.data_ini) formData.data_ini = dayjs(formData.data_ini).format('YYYY-MM-DDTHH:mm:ss');
            if (formData.data_fi) formData.data_fi = dayjs(formData.data_fi).format('YYYY-MM-DDTHH:mm:ss');

            // Ensure fields that might be missing in form are preserved if logical, or rely on backend update
            const payload: any = {
                ...props.element, // Keep original fields like descripcio
                ...formData,      // Override with form values
                id: props.element.id
            };

            if (fileList.length > 0) {
                payload.img = imageUrl; // Pass base64 image only if changed
            }

            console.log("Payload:", payload);

            await PostProjecte(payload, userId + "");
            props.onClose(true);
        } catch (error) {
            console.error("Validation failed:", error);
        }
    }

    const handleCancel = () => {
        props.onClose(false);
    };

    const options = [
        { value: 0, label: "No visible" },
        { value: 1, label: "En curs" },
        { value: 2, label: "Finalitzat" },
        { value: 3, label: "Tancat" }
    ];

    return (
        <ConfigProvider locale={locale}>
            <Modal
                title={<Typography.Title level={4} style={{ margin: 0 }}>Configurar Projecte</Typography.Title>}
                open={props.visible}
                onCancel={handleCancel}
                onOk={handleSubmit}
                width={600}
                footer={[
                    <Button key="cancel" onClick={handleCancel}>
                        Cancel·lar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        onClick={handleSubmit}
                        style={{ backgroundColor: AvaluacioColors.colors.color6 }}
                    >
                        Guardar
                    </Button>,
                ]}
            >
                <Form
                    form={form}
                    layout="vertical"
                    initialValues={{ estat: 0 }}
                    style={{ marginTop: 24 }}
                >
                    <Form.Item label="ID" name="id" hidden>
                        <Input />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item
                                label="Nom del Projecte"
                                name="nom"
                                rules={[{ required: true, message: 'El nom és obligatori' }]}
                            >
                                <Input placeholder="Introdueix el nom del projecte" />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Estat"
                                name="estat"
                                rules={[{ required: true, message: 'Escull un estat' }]}
                            >
                                <Select
                                    placeholder="Selecciona estat"
                                    disabled={!(user?.tipus === "1" || user?.tipus === "0")}
                                    options={options}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Escala de Validació"
                                name="escala_validacio"
                                rules={[{ required: true, message: 'Selecciona una escala' }]}
                                initialValue={5}
                            >
                                <Select
                                    placeholder="Selecciona escala"
                                    options={[
                                        { value: 3, label: "3 (Caras: 😟, 😐, 😊)" },
                                        { value: 5, label: "5 (Estrellas)" },
                                        { value: 10, label: "10 (Numérica / Slider)" }
                                    ]}
                                />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Aules"
                                name="id_aules"
                                rules={[{ required: true, message: 'Selecciona almenys una aula' }]}
                            >
                                <Select
                                    mode="multiple"
                                    placeholder="Selecciona aules"
                                    options={aules.map((a: any) => ({ value: a.id, label: a.nom }))}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item label="Imatge">
                                <Space>
                                    <Upload
                                        fileList={fileList}
                                        onChange={handleUploadChange}
                                        beforeUpload={() => false} // Manual upload
                                        maxCount={1}
                                        listType="picture"
                                        showUploadList={false}
                                    >
                                        <Button icon={<UploadOutlined />}>Pujar Imatge</Button>
                                    </Upload>
                                    {imageUrl && (
                                        <img src={imageUrl} alt="preview" style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 8 }} />
                                    )}
                                </Space>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                label="Data Inici"
                                name="data_ini"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    showTime={{ format: 'HH:mm' }}
                                    placeholder="Selecciona data inici"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                label="Data Fi"
                                name="data_fi"
                            >
                                <DatePicker
                                    style={{ width: '100%' }}
                                    format="DD/MM/YYYY"
                                    showTime={{ format: 'HH:mm' }}
                                    placeholder="Selecciona data fi"
                                />
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </ConfigProvider>
    );
};

export default ProjecteModal;
